/**
 * Recommendation Detail Page
 *
 * Was previously a popup dialog opened over the Recommendations list. Moved
 * to a real route (/recommendations/:id) instead - this view has five tabs,
 * a live streaming pipeline, and charts; that's page-scale content, not
 * dialog-scale, and a dedicated route also makes it bookmarkable/shareable
 * and safe to refresh, which a popup never was.
 *
 * Deliberately fetches its own data by id rather than receiving the
 * recommendation via router state passed from the list page - a page that
 * only works when navigated to from one specific place (and breaks on a
 * direct link or a refresh) isn't really a page, it's a modal wearing a URL.
 */

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertTriangle } from 'lucide-react';
import { Badge, BadgeVariant } from '../components/common/Badge';
import { Recommendation, getTypeLabel, getTypeColor, NATIVE_ADVISOR_LABELS } from './RecommendationsPage';
import { RecommendationDetailBody } from '../components/recommendations/RecommendationInvestigationPanel';
import { LifecycleBar, StateTimeline, useStateCatalogue } from '../components/recommendations/RecommendationLifecycle';
import { attachAuthRefresh } from '../services/apiClient';
import { getActiveTenantId } from '../services/tenantContext';

const OPTIMIZATION_API_URL = import.meta.env.VITE_OPTIMIZATION_API_URL || 'http://localhost:3005';
const optimizationApi = attachAuthRefresh(axios.create({ baseURL: OPTIMIZATION_API_URL }));

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('accessToken');
  return {
    'x-tenant-id': getActiveTenantId(),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

const RecommendationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const catalogue = useStateCatalogue();

  const [rec, setRec] = useState<Recommendation | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [phase, setPhase] = useState<'classifying' | 'generating' | 'confirming' | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  // Bumped after every state change so the item and its timeline reload
  // from the server (the source of truth) instead of guessing locally.
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    if (reloadKey === 0) setLoading(true);
    setNotFound(false);
    optimizationApi
      .get(`/api/v1/recommendations/${id}`, { headers: authHeaders() })
      .then((res) => {
        if (cancelled) return;
        setRec(res.data?.data || null);
        if (!res.data?.data) setNotFound(true);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.response?.status === 404) setNotFound(true);
        else setError(err.response?.data?.message || err.message || 'Failed to load recommendation');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, reloadKey]);

  const handleLifecycleChanged = (msg?: string) => {
    setError(null);
    if (msg) setMessage(msg);
    setReloadKey((k) => k + 1);
  };

  const handleInvestigate = async (recId: string) => {
    setBusy(true);
    setPhase('classifying');
    setError(null);
    setMessage(null);
    try {
      const response = await optimizationApi.post(
        `/api/v1/recommendations/${recId}/approve`,
        {},
        // The user is taken from the login on the server (gap 18).
        { headers: { 'Idempotency-Key': crypto.randomUUID(), ...authHeaders() } }
      );
      const newStatus = response.data?.data?.status;
      const approval = response.data?.data?.approval_action;
      const skippedReason = response.data?.data?.approval_action_skipped_reason;

      if (skippedReason) {
        setMessage(`Investigated. Automated preview unavailable: ${skippedReason}`);
        setRec((prev) => (prev ? { ...prev, status: newStatus } : prev));
      } else if (approval) {
        setPhase('generating');
        // Same staged reveal the list page used - the backend already
        // computed classification + script/SOP in this one response; this is
        // a presentation choice for legibility, not a claim they happened at
        // genuinely different times.
        setRec((prev) => (prev ? { ...prev, status: newStatus, metadata: { ...prev.metadata, approval_action: { ...approval, artifact: undefined } } } : prev));
        await new Promise((resolve) => setTimeout(resolve, 500));
        setRec((prev) => (prev ? { ...prev, metadata: { ...prev.metadata, approval_action: approval } } : prev));
      }
      // Open -> In review is now recorded server-side; reload state + history.
      setReloadKey((k) => k + 1);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to run investigation');
    } finally {
      setPhase(undefined);
      setBusy(false);
    }
  };

  const handleConfirm = async (recId: string) => {
    setBusy(true);
    setPhase('confirming');
    setError(null);
    setMessage(null);
    try {
      const response = await optimizationApi.post(
        `/api/v1/recommendations/${recId}/confirm-action`,
        {},
        { headers: { 'Idempotency-Key': crypto.randomUUID(), ...authHeaders() } }
      );
      const outcome = response.data?.data?.approval_action;
      const newStatus = response.data?.data?.status;
      setRec((prev) => (prev ? { ...prev, status: newStatus || prev.status, metadata: { ...prev.metadata, approval_action: outcome } } : prev));

      if (outcome?.l1_execution) {
        const exec = outcome.l1_execution;
        setMessage(
          exec.status === 'executed'
            ? `Executed automatically. Audit ID: ${exec.audit_id || 'n/a'}`
            : `Not executed (${exec.status})${exec.block_reason ? `: ${exec.block_reason}` : ''}`
        );
      } else if (outcome?.ticket) {
        setMessage(
          `Ticket opened${outcome.ticket.external_key ? `: ${outcome.ticket.external_key}` : ''}${outcome.ticket.external_url ? ` (${outcome.ticket.external_url})` : ''}`
        );
      }
      setReloadKey((k) => k + 1);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to confirm action');
    } finally {
      setPhase(undefined);
      setBusy(false);
    }
  };

  const advisorKey = rec?.metadata?.confirmed_by_native_advisor || rec?.metadata?.source_system;
  const hasFriendlyName = Boolean(rec?.resource_name) && rec?.resource_name !== rec?.resource_id;

  return (
    <div className="mx-auto max-w-5xl">
      <button
        onClick={() => navigate('/recommendations')}
        className="mb-3 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground dark:text-muted-foreground/70 dark:hover:text-muted-foreground/70"
      >
        <ArrowLeft className="h-4 w-4" /> Back to Recommendations
      </button>

      {loading && (
        <div className="flex items-center gap-2 py-16 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}

      {!loading && notFound && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          This recommendation could not be found - check the link, or that you are viewing the right company.
        </div>
      )}

      {!loading && rec && (
        <>
          <div className="mb-4">
            <h1 className="text-lg font-semibold tracking-tight text-foreground">
              {rec.resource_name || rec.resource_id}
            </h1>
            {hasFriendlyName && (
              <p className="mt-0.5 truncate font-mono text-xs text-muted-foreground">{rec.resource_id}</p>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <span className={`px-2 py-0.5 text-xs font-medium rounded ${getTypeColor(rec.type)}`}>{getTypeLabel(rec.type)}</span>
              <Badge variant={rec.provider.toLowerCase() as BadgeVariant}>{rec.provider.toUpperCase()}</Badge>
              {advisorKey && <Badge variant="success">✓ Confirmed by {NATIVE_ADVISOR_LABELS[advisorKey] || advisorKey}</Badge>}
            </div>
          </div>

          {message && (
            <div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-200">
              {message}
            </div>
          )}
          {error && (
            <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm text-rose-800 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-200">
              {error}
            </div>
          )}

          {/* Gap 21: the item's one state, its details and only the moves
              allowed now - above the tabs, so it is the first thing seen. */}
          <LifecycleBar rec={rec} catalogue={catalogue} onChanged={handleLifecycleChanged} />

          <RecommendationDetailBody rec={rec} phase={phase} onInvestigate={handleInvestigate} onConfirm={handleConfirm} isBusy={busy} catalogue={catalogue} />

          <div className="mt-4 rounded-lg border border-border bg-card p-4">
            <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">History</div>
            <StateTimeline recId={rec.id} catalogue={catalogue} refreshKey={reloadKey} />
          </div>
        </>
      )}
    </div>
  );
};

export default RecommendationDetailPage;
